import type {
  Comment,
  ListFilters,
  Paginated,
  Permissions,
  Stats,
  Suggestion,
  SuggestionDetail,
  User,
} from './types';

/**
 * In dev, Vite proxies /api to the backend so the browser stays on one origin
 * and the httpOnly session cookie is first-party. In production, set
 * VITE_API_URL if the API lives on a different host.
 */
const BASE = import.meta.env.VITE_API_URL ?? '';

export class ApiError extends Error {
  status: number;
  code: string;
  /** Per-field messages from server-side Zod validation. */
  fields?: Record<string, string>;

  constructor(status: number, message: string, code = 'error', fields?: Record<string, string>) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.fields = fields;
  }
}

async function request<T>(
  path: string,
  opts: { method?: string; body?: unknown; form?: FormData; signal?: AbortSignal } = {},
): Promise<T> {
  const headers: Record<string, string> = {};
  let body: BodyInit | undefined;

  if (opts.form) {
    body = opts.form; // let the browser set the multipart boundary
  } else if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(opts.body);
  }

  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      method: opts.method ?? 'GET',
      headers,
      body,
      signal: opts.signal,
      credentials: 'include',
    });
  } catch (err) {
    if ((err as Error).name === 'AbortError') throw err;
    throw new ApiError(
      0,
      'Cannot reach the server. Check that the API is running on port 4000.',
      'network_error',
    );
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let payload: any = null;
  try {
    payload = text ? JSON.parse(text) : null;
  } catch {
    throw new ApiError(res.status, 'The server returned an unreadable response.', 'bad_response');
  }

  if (!res.ok) {
    const e = payload?.error ?? {};
    throw new ApiError(
      res.status,
      e.message ?? 'Something went wrong. Please try again.',
      e.code ?? 'error',
      e.fields,
    );
  }

  return payload as T;
}

/** Turns the filter object into the query string the API expects. */
export function filtersToQuery(f: ListFilters, pageSize = 24): string {
  const p = new URLSearchParams();
  if (f.product.length) p.set('product', f.product.join(','));
  if (f.category.length) p.set('category', f.category.join(','));
  if (f.status.length) p.set('status', f.status.join(','));
  if (f.visibility) p.set('visibility', f.visibility);
  if (f.assignedToId) p.set('assignedToId', f.assignedToId);
  if (f.scope !== 'all') p.set('scope', f.scope);
  if (f.q.trim()) p.set('q', f.q.trim());
  if (f.minPriority > 0) p.set('minPriority', String(f.minPriority));
  p.set('sort', f.sort);
  p.set('page', String(f.page));
  p.set('pageSize', String(pageSize));
  return p.toString();
}

export const api = {
  /* auth */
  signup: (body: { name: string; email: string; password: string }) =>
    request<{ user: User; token: string }>('/api/auth/signup', { method: 'POST', body }),

  login: (body: { email: string; password: string }) =>
    request<{ user: User; token: string }>('/api/auth/login', { method: 'POST', body }),

  logout: () => request<{ ok: true }>('/api/auth/logout', { method: 'POST' }),

  me: (signal?: AbortSignal) => request<{ user: User }>('/api/auth/me', { signal }),

  /* people */
  users: () => request<{ users: User[] }>('/api/users'),

  /* suggestions */
  list: (query: string, signal?: AbortSignal) =>
    request<Paginated<Suggestion>>(`/api/suggestions?${query}`, { signal }),

  stats: (signal?: AbortSignal) => request<Stats>('/api/suggestions/stats', { signal }),

  get: (id: string, signal?: AbortSignal) =>
    request<{ suggestion: SuggestionDetail; permissions: Permissions }>(
      `/api/suggestions/${id}`,
      { signal },
    ),

  create: (form: FormData) =>
    request<{ suggestion: Suggestion }>('/api/suggestions', { method: 'POST', form }),

  update: (id: string, body: Record<string, unknown>) =>
    request<{ suggestion: Suggestion }>(`/api/suggestions/${id}`, { method: 'PATCH', body }),

  assign: (id: string, assignedToId: string | null, note?: string) =>
    request<{ suggestion: Suggestion }>(`/api/suggestions/${id}/assign`, {
      method: 'POST',
      body: { assignedToId, note },
    }),

  setStatus: (id: string, status: string, note?: string) =>
    request<{ suggestion: Suggestion }>(`/api/suggestions/${id}/status`, {
      method: 'POST',
      body: { status, note },
    }),

  comment: (id: string, body: string) =>
    request<{ comment: Comment }>(`/api/suggestions/${id}/comments`, {
      method: 'POST',
      body: { body },
    }),

  remove: (id: string) => request<{ ok: true }>(`/api/suggestions/${id}`, { method: 'DELETE' }),
};
